import { Link } from "react-router-dom";
import {
  ArrowRight,
  Globe,
  ShoppingBag,
  LayoutTemplate,
  RefreshCw,
  Wrench,
  Zap,
  Smartphone,
  Search,
  Target,
  PoundSterling,
  Check,
  Star,
  Quote,
} from "lucide-react";
import AuraGlow from "@/components/AuraGlow";
import FadeIn from "@/components/FadeIn";
import design from "@/lib/images";

const services = [
  {
    icon: Globe,
    title: "Business Website Design",
    desc: "Premium multi-page sites that build instant trust and turn visitors into customers.",
  },
  {
    icon: ShoppingBag,
    title: "E-commerce Websites",
    desc: "Fast, modern online stores built to sell — clean checkouts and product flows that convert.",
  },
  {
    icon: LayoutTemplate,
    title: "Landing Pages",
    desc: "Single-page conversion machines for campaigns, launches and lead generation.",
  },
  {
    icon: RefreshCw,
    title: "Website Redesigns",
    desc: "Reframe an outdated site into a modern, high-performing brand experience.",
  },
  {
    icon: Wrench,
    title: "Maintenance & Support",
    desc: "Ongoing care so your site stays fast, secure and performing — month after month.",
  },
];

const reasons = [
  { icon: Zap, title: "Fast delivery", desc: "Most projects launched in 3–6 days." },
  { icon: Smartphone, title: "Mobile-first design", desc: "Pixel-perfect on every screen size." },
  { icon: Search, title: "SEO-ready", desc: "Built on a search-friendly foundation from day one." },
  { icon: Target, title: "Conversion focused", desc: "Every layout tuned to turn visitors into enquiries." },
  { icon: PoundSterling, title: "Affordable", desc: "Premium results at small-business friendly prices." },
];

const tiers = [
  {
    name: "Starter",
    price: "£299–£499",
    headline: "One-page launch site",
    features: [
      "Single-page website",
      "Mobile responsive",
      "Basic on-page SEO",
      "Contact section",
      "Delivery in ~3 days",
    ],
    cta: "Start with Starter",
    highlight: false,
  },
  {
    name: "Business",
    price: "£599–£999",
    headline: "Most popular",
    features: [
      "Multi-page website",
      "Modern bespoke design",
      "Contact forms",
      "SEO optimisation",
      "Delivery in ~6 days",
    ],
    cta: "Choose Business",
    highlight: true,
  },
  {
    name: "Premium",
    price: "£1000+",
    headline: "Fully custom build",
    features: [
      "Full custom design",
      "Advanced features & integrations",
      "Branding support",
      "Priority support",
      "Performance + SEO tuning",
    ],
    cta: "Go Premium",
    highlight: false,
  },
];


export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 md:pt-40 pb-24 md:pb-32" data-testid="hero-section">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <AuraGlow className="left-1/2 top-[10%] -translate-x-1/2" size={420} blur={90} opacity={0.12} />
        <AuraGlow className="left-[18%] top-[70%] -translate-x-1/2 -translate-y-1/2" size={240} blur={70} opacity={0.08} color="#7BB6FF" />
        <div className="container-7 relative">
          <FadeIn>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#1E293B] bg-[#111827] px-3 py-1 text-xs text-[#94A3B8]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#4DA3FF] animate-pulse" />
              UK-based web design studio · Booking now
            </div>
          </FadeIn>
          <FadeIn delay={120}>
            <h1 className="mt-6 max-w-4xl text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight">
              Modern websites that help businesses
              <span className="block gradient-text">look premium &amp; get more customers.</span>
            </h1>
          </FadeIn>
          <FadeIn delay={220}>
            <p className="mt-6 max-w-2xl text-lg text-[#94A3B8] leading-relaxed">
              We design and build fast, modern, high-converting websites for small
              businesses and growing brands in the UK.
            </p>
          </FadeIn>
          <FadeIn delay={320}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="btn-primary" data-testid="hero-cta-primary">
                Get a Website
                <ArrowRight size={16} />
              </Link>
              <Link to="/services" className="btn-secondary" data-testid="hero-cta-secondary">
                View Services
            </Link>
            </div>
          </FadeIn>

          <FadeIn delay={460}>
            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
              {[
                { k: "Modern", v: "Custom-built websites" },
                { k: "3–6d", v: "Average delivery" },
                { k: "98%", v: "Client satisfaction" },
                { k: "UK", v: "Designed & built" },
              ].map((s) => (
                <div key={s.v} className="surface-card p-5">
                  <p className="font-display text-2xl text-white">{s.k}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">{s.v}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-32 relative" data-testid="services-section">
        <div className="container-7">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <p className="overline">What we do</p>
              <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-medium">
                Websites built for outcomes — not just looks.
              </h2>
            </div>
            <p className="text-[#94A3B8] max-w-md">
              Five focused services covering the full spectrum of what modern
              businesses actually need online.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <FadeIn key={s.title} delay={i * 80}>
                <div
                  className="surface-card hover-card p-7 h-full relative overflow-hidden group"
                  data-testid={`service-card-${i}`}
                >
                  <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#4DA3FF]/10 blur-2xl opacity-0 group-hover:opacity-100 transition" />
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#1A2235] border border-[#1E293B] text-[#4DA3FF]">
                    <s.icon size={20} strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-5 text-xl font-medium">{s.title}</h3>
                  <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 md:py-28 bg-[#0E1422] border-y border-[#1E293B] relative overflow-hidden" data-testid="why-us-section">
        <AuraGlow className="-right-40 top-1/2 -translate-y-1/2" size={520} blur={160} opacity={0.12} />
        <div className="container-7 relative">
          <div className="max-w-2xl">
            <p className="overline">Why WebAuraSites</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-medium">
              A studio built around small businesses.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {reasons.map((r, i) => (
              <FadeIn key={r.title} delay={i * 80}>
                <div className="surface-card p-6 h-full" data-testid={`reason-card-${i}`}>
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#4DA3FF]/10 text-[#4DA3FF]">
                    <r.icon size={18} strokeWidth={1.6} />
                  </div>
                  <h4 className="mt-4 font-medium">{r.title}</h4>
                  <p className="mt-2 text-sm text-[#94A3B8]">{r.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>


      {/* ABOUT SNIPPET */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="about-snippet-section">
        <div className="container-7 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="overline">About WebAuraSites</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-medium">
              A small UK studio with a big standard for the web.
            </h2>
            <p className="mt-6 text-[#94A3B8] leading-relaxed max-w-2xl">
              WebAuraSites is a UK-based web design studio focused on helping small
              businesses build a strong online presence and attract more customers
              through modern, high-performing websites — without the agency price tag,
              the long timelines or the bloated process.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/about" className="btn-secondary" data-testid="about-snippet-cta">
                Read our story
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <AuraGlow className="-left-10 top-10" size={300} blur={120} opacity={0.18} />
              <div className="surface-card overflow-hidden">
                <img
                  src={design.image_urls.about_us_office.url}
                  alt="Modern workspace"
                  loading="lazy"
                  className="w-full h-[360px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 md:py-32 bg-[#0E1422] border-y border-[#1E293B]" data-testid="pricing-section">
        <div className="container-7">
          <div className="max-w-2xl">
            <p className="overline">Pricing</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-medium">
              Transparent pricing for every stage of business.
            </h2>
            <p className="mt-4 text-[#94A3B8]">
              Pick a starting point — we'll tailor scope and quote during your free consultation.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((t, i) => (
              <FadeIn key={t.name} delay={i * 100}>
                <div
                  className={`surface-card hover-card p-8 h-full relative overflow-hidden ${
                    t.highlight ? "border-[#4DA3FF]/60" : ""
                  }`}
                  data-testid={`pricing-tier-${t.name.toLowerCase()}`}
                >
                  {t.highlight && (
                    <span
                      className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.2em] font-medium text-[#0B0F1A] bg-[#4DA3FF] px-2.5 py-1 rounded-full"
                      data-testid="pricing-most-popular-badge"
                    >
                      Most popular
                    </span>
                  )}
                  <p className="overline">{t.headline}</p>
                  <h3 className="mt-3 font-display text-2xl font-medium">{t.name}</h3>
                  <p className="mt-2 text-3xl font-display text-white">{t.price}</p>
                  <ul className="mt-6 space-y-3 text-sm text-[#94A3B8]">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check size={16} className="text-[#4DA3FF] mt-0.5 shrink-0" strokeWidth={2} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className={`mt-8 w-full ${t.highlight ? "btn-primary" : "btn-secondary"}`}
                    data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                  >
                    {t.cta}
                  </Link>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>


      {/* FINAL CTA */}
      <section className="py-20 md:py-28" data-testid="final-cta-section">
        <div className="container-7">
          <div className="relative overflow-hidden surface-card p-10 md:p-16">
            <AuraGlow className="left-[20%] top-[40%]" size={520} blur={160} opacity={0.18} />
            <div className="relative max-w-3xl">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium">
                Ready to upgrade your business online?
              </h2>
              <p className="mt-5 text-[#94A3B8] max-w-xl">
                Let's design a website your business actually deserves — clean, fast,
                and built to convert.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary" data-testid="final-cta-start">
                  Get Started
                  <ArrowRight size={16} />
                </Link>
                <Link to="/contact" className="btn-secondary" data-testid="final-cta-consult">
                  Book a Free Consultation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
